import React, { useState } from 'react';
import { motion } from 'framer-motion';
import type { ProductConfig } from '../customizerConfig';

interface TearSheetSlideProps {
  product: ProductConfig;
  imageUrl: string;
}

const CM_FACTOR = 2.54;

function inchesToCm(value: string): string {
  return value
    .replace(/(\d+)\s*(\d+)\/(\d+)/g, (_m, whole, num, den) => {
      const inches = parseInt(whole) + parseInt(num) / parseInt(den);
      return `${(inches * CM_FACTOR).toFixed(1)}`;
    })
    .replace(/(\d+\.?\d*)\s*"/g, (_m, num) => {
      return `${(parseFloat(num) * CM_FACTOR).toFixed(1)} cm`;
    })
    .replace(/(\d+\.?\d*)\s*lbs\.?/gi, (_m, num) => {
      return `${(parseFloat(num) * 0.4536).toFixed(1)} kg`;
    });
}

const TearSheetSlide: React.FC<TearSheetSlideProps> = ({ product, imageUrl }) => {
  const [unit, setUnit] = useState<'in' | 'cm'>('in');

  const displayRows = product.dimensionsTable.map((row) => ({
    label: row.label,
    value: unit === 'cm' ? inchesToCm(row.value) : row.value,
  }));

  return (
    <div className="w-full h-full flex flex-col overflow-hidden rounded-2xl">
      <div className="relative flex" style={{ height: '80%', background: '#eae6df' }}>
        <div className="absolute top-3.5 left-5 z-10">
          <img
            src="/assets/images/ralph_logo.png"
            alt="Ralph Lauren"
            className="h-12 object-contain"
            draggable={false}
          />
        </div>

        <div className="flex items-center justify-center" style={{ width: '58%' }}>
          <motion.img
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            src={imageUrl}
            alt="Product Specification"
            className="w-full h-full object-contain pt-14 pb-3 px-3"
            draggable={false}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, x: 8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="flex-1 flex flex-col justify-center pl-4 pr-6"
        >
          <h3
            className="text-[15px] font-bold mb-4"
            style={{ color: '#0c1a2e', fontFamily: "'Georgia', 'Times New Roman', serif" }}
          >
            Product
          </h3>

          {displayRows.map((row, idx) => (
            <div key={idx}>
              <div className="flex justify-between items-baseline py-2.5 gap-4">
                <span className="text-[12px] font-semibold whitespace-nowrap" style={{ color: '#0c1a2e' }}>
                  {row.label}
                </span>
                <span className="text-[12px] font-medium text-right" style={{ color: '#3a3a3a' }}>
                  {row.value}
                </span>
              </div>
              {idx < displayRows.length - 1 && (
                <div className="h-px" style={{ background: 'linear-gradient(90deg, #c0b6a4 0%, #d6ccbc 60%, transparent 100%)' }} />
              )}
            </div>
          ))}

          <div className="h-px mt-2" style={{ background: 'linear-gradient(90deg, #c0b6a4 0%, #d6ccbc 60%, transparent 100%)' }} />

          <div className="flex items-center justify-end gap-2.5 mt-3">
            <button
              onClick={() => setUnit('in')}
              className={`text-[10px] font-bold tracking-widest transition-colors duration-200 cursor-pointer ${unit === 'in' ? 'text-[#0c1a2e]' : 'text-[#a09880] hover:text-[#6a6050]'}`}
            >
              IN
            </button>
            <span className="text-[10px]" style={{ color: '#c0b8a8' }}>|</span>
            <button
              onClick={() => setUnit('cm')}
              className={`text-[10px] font-bold tracking-widest transition-colors duration-200 cursor-pointer ${unit === 'cm' ? 'text-[#0c1a2e]' : 'text-[#a09880] hover:text-[#6a6050]'}`}
            >
              CM
            </button>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.15 }}
        className="flex flex-col justify-center px-8"
        style={{ height: '20%', background: '#0d0c0a' }}
      >
        <p className="text-[13px] font-bold tracking-[0.3em] mb-2" style={{ color: '#c9a96e' }}>
          {product.tearSheetTagline}
        </p>
        <p className="text-[14px] leading-[1.75]" style={{ color: 'rgba(255,255,255,0.72)' }}>
          {product.tearSheetDescription}
        </p>
      </motion.div>
    </div>
  );
};

export default TearSheetSlide;
